import { QuestActivityEventType, type Quest } from '../types/quest';
import { SlackNotificationEventType } from '../types/srtNotification';

export const QUEST_BROWSER_NOTIFICATION_EVENT_TYPES: SlackNotificationEventType[] =
    [
        SlackNotificationEventType.HUMAN_INPUT_REQUESTED,
        SlackNotificationEventType.TASK_COMPLETED,
        SlackNotificationEventType.STATUS_CHANGED,
        SlackNotificationEventType.PLAN_ITEM_ADDED,
    ];

export const isQuestBrowserNotificationEnabled = (
    quest: Quest,
    notificationEventType: SlackNotificationEventType,
): boolean =>
    quest.notificationSettings.browser?.eventTypes.includes(
        notificationEventType,
    ) ?? false;

const QUEST_ACTIVITY_EVENT_TITLES: Record<QuestActivityEventType, string> = {
    [QuestActivityEventType.PROJECT_CREATED]: 'Quest created',
    [QuestActivityEventType.PROJECT_PAUSED]: 'Quest paused',
    [QuestActivityEventType.PROJECT_RESUMED]: 'Quest resumed',
    [QuestActivityEventType.PROJECT_STEERED]: 'Quest steered',
    [QuestActivityEventType.PROJECT_NUDGED]: 'Quest nudged',
    [QuestActivityEventType.OBJECTIVE_UPDATED]: 'Objective updated',
    [QuestActivityEventType.PLAN_UPDATED]: 'Plan updated',
    [QuestActivityEventType.TASK_STARTED]: 'Task started',
    [QuestActivityEventType.TASK_WAITING]: 'Task waiting',
    [QuestActivityEventType.TASK_HANDOFF_RECEIVED]: 'Task handoff received',
    [QuestActivityEventType.TASK_COMPLETED]: 'Task completed',
    [QuestActivityEventType.TASK_FAILED]: 'Task failed',
    [QuestActivityEventType.HUMAN_INPUT_REQUESTED]: 'Input requested',
    [QuestActivityEventType.HUMAN_INPUT_ANSWERED]: 'Input answered',
    [QuestActivityEventType.AUDIENCE_CREATED]: 'Audience created',
    [QuestActivityEventType.AUDIENCE_UPDATED]: 'Audience updated',
    [QuestActivityEventType.CAMPAIGN_CREATED]: 'Campaign created',
    [QuestActivityEventType.CAMPAIGN_UPDATED]: 'Campaign updated',
    [QuestActivityEventType.CAMPAIGN_SENT]: 'Campaign sent',
    [QuestActivityEventType.JOURNEY_CREATED]: 'Journey created',
    [QuestActivityEventType.JOURNEY_UPDATED]: 'Journey updated',
    [QuestActivityEventType.CUSTOM_ATTRIBUTE_CREATED]: 'Attribute created',
    [QuestActivityEventType.CUSTOM_ATTRIBUTE_UPDATED]: 'Attribute updated',
};

const QUEST_TASK_COMPLETED_ACTIVITY_EVENTS = new Set<QuestActivityEventType>([
    QuestActivityEventType.TASK_COMPLETED,
    QuestActivityEventType.TASK_HANDOFF_RECEIVED,
    QuestActivityEventType.AUDIENCE_CREATED,
    QuestActivityEventType.AUDIENCE_UPDATED,
    QuestActivityEventType.CAMPAIGN_CREATED,
    QuestActivityEventType.CAMPAIGN_UPDATED,
    QuestActivityEventType.CAMPAIGN_SENT,
    QuestActivityEventType.JOURNEY_CREATED,
    QuestActivityEventType.JOURNEY_UPDATED,
    QuestActivityEventType.CUSTOM_ATTRIBUTE_CREATED,
    QuestActivityEventType.CUSTOM_ATTRIBUTE_UPDATED,
]);

export const getQuestActivityEventTitle = (
    eventType: QuestActivityEventType,
): string => QUEST_ACTIVITY_EVENT_TITLES[eventType];

export const mapQuestActivityToNotificationEventType = (
    eventType: QuestActivityEventType,
): SlackNotificationEventType => {
    switch (eventType) {
        case QuestActivityEventType.HUMAN_INPUT_REQUESTED:
            return SlackNotificationEventType.HUMAN_INPUT_REQUESTED;
        case QuestActivityEventType.HUMAN_INPUT_ANSWERED:
            return SlackNotificationEventType.HUMAN_INPUT_ANSWERED;
        case QuestActivityEventType.PLAN_UPDATED:
            return SlackNotificationEventType.PLAN_ITEM_ADDED;
        default:
            if (QUEST_TASK_COMPLETED_ACTIVITY_EVENTS.has(eventType)) {
                return SlackNotificationEventType.TASK_COMPLETED;
            }
            return SlackNotificationEventType.STATUS_CHANGED;
    }
};
